/**
 * Component areas for the Raise Issue form.
 *
 * Each top-level area has a stable `code` (used in the GitHub issue
 * body so engineers can grep / triage by component) and a display
 * `label`. Sub-areas are optional — when present, they narrow the
 * scope (e.g. RM UI → Sites page).
 *
 * **Maintenance contract:** if you add/rename/remove an item in the
 * sidebar (`sections[]` in `components/shell/AppShell.tsx`), update
 * `RM_UI_SUB_AREAS` below so the issue form's picker stays in sync.
 * The reverse — adding a sub-area here that isn't in the nav —
 * is fine and expected (e.g. `login-bootstrap`, `cross-cutting`).
 *
 * The values flow:
 *   IssueFormDialog → multipart "area" + "subArea"
 *     → POST /api/v1/issues (CreateIssueRequest)
 *       → GitHubIssueService.BuildIssueBody → rendered in the GH body.
 */

export type SubArea = {
  /** Stable, short, lowercase-kebab. Lands verbatim in GitHub. */
  code: string;
  label: string;
};

export type ComponentArea = {
  code: string;
  label: string;
  /** Optional. If absent, the form skips the second select. */
  subAreas?: SubArea[];
};

/**
 * Mirrors AppShell.tsx `sections[]` order. Sections collapsed into a
 * flat list (form picker is one select, not nested) — section name
 * prepended where it disambiguates (e.g. "ARMS Staff — Site controllers"
 * is the lifecycle-admin page, distinct from the operator Sites view).
 */
const RM_UI_SUB_AREAS: SubArea[] = [
  // Operations section
  { code: 'home',                     label: 'Home' },
  { code: 'operators',                label: 'Operators' },
  { code: 'sites',                    label: 'Sites' },
  { code: 'machines',                 label: 'Machines' },
  { code: 'iot-devices',              label: 'IoT devices (HATs)' },
  // Consolidated operator-aware SC surface (the old heartbeat-only "Fleet"
  // page + the ARMS-only lifecycle-admin page folded into one).
  { code: 'site-controllers',         label: 'Site controllers' },
  // SC SSH device-cert recovery approval surface (Phase-1 Task I1).
  { code: 'sc-recovery-approvals',    label: 'Site controllers — Recovery approvals' },
  // SC SSH Phase-2 CA rotation control surface (Task C2).
  { code: 'sc-ca-rotation',           label: 'Site controllers — CA rotation' },
  { code: 'machine-participation',    label: 'Machine Vendors' },
  { code: 'approvals',                label: 'Approvals' },
  { code: 'audit-log',                label: 'Audit log' },
  { code: 'issues',                   label: 'Issues' },

  // ARMS Staff section
  { code: 'qr-header-templates',      label: 'ARMS Staff — QR Code header templates' },
  { code: 'sc-qr-batches',            label: 'ARMS Staff — SC QR batches' },
  { code: 'iot-qr-batches',           label: 'ARMS Staff — IoT (HAT) QR batches' },
  { code: 'cabinet-qr-batches',       label: 'ARMS Staff — Machine QR batches' },
  { code: 'release-catalogue',        label: 'ARMS Staff — Release catalogue' },

  // Cash desk section (stage 1 — line-of-sight AFT).
  { code: 'cashdesk-terminals',       label: 'Cash desk — Terminals' },
  { code: 'cashdesk-options',         label: 'Cash desk — Options' },
  { code: 'cashdesk-floor-layout',    label: 'Cash desk — Floor layout' },
  { code: 'cashdesk-activities',      label: 'Cash desk — Activities' },

  // Access control section
  { code: 'users',                    label: 'Users' },
  { code: 'roles',                    label: 'Roles' },
  { code: 'permissions',              label: 'Permissions' },

  // Settings section
  { code: 'appearance',               label: 'Appearance' },
  { code: 'system-settings',          label: 'System settings' },
  { code: 'operator-branding',        label: 'Operator branding' },

  // Outside the AppShell layout
  { code: 'login-bootstrap',          label: 'Login / Bootstrap' },
  { code: 'cross-cutting',            label: 'Cross-cutting / other portal' },
];

export const COMPONENT_AREAS: ComponentArea[] = [
  {
    code:  'rm-ui',
    label: 'Route Master Portal (UI)',
    subAreas: RM_UI_SUB_AREAS,
  },
  {
    code:  'api',
    label: 'API (Route.Api backend)',
    subAreas: [
      { code: 'auth',                   label: 'Auth (login / JWT / MFA)' },
      { code: 'users-roles-permissions', label: 'Users / Roles / Permissions' },
      { code: 'operators',              label: 'Operators' },
      { code: 'sites',                  label: 'Sites' },
      { code: 'machines',               label: 'Machines + maintenance' },
      { code: 'site-controllers',       label: 'Site controllers' },
      { code: 'provisioning-factory',   label: 'Provisioning / Factory' },
      { code: 'releases',               label: 'Releases + rollouts' },
      { code: 'stickers',               label: 'Stickers / QR codes' },
      { code: 'heartbeats-fleet',       label: 'Heartbeats / Fleet' },
      { code: 'audit',                  label: 'Audit log' },
      { code: 'reports',                label: 'Reports endpoints' },
      { code: 'payment-gateway',        label: 'Payment Gateway (partner adapter)' },
      { code: 'issues',                 label: 'Issues / pipeline' },
      { code: 'mqtt-broker',            label: 'MQTT broker integration' },
      { code: 'cross-cutting',          label: 'Cross-cutting (middleware, OTEL, RLS)' },
    ],
  },
  {
    code:  'site-controller',
    label: 'Site Controller',
    subAreas: [
      { code: 'commissioning-agent',    label: 'Commissioning agent (factory-agent)' },
      { code: 'runtime',                label: 'Runtime container (sc-runtime)' },
      { code: 'local-api',              label: 'Local API (HTTPS :8443)' },
      { code: 'mtls-cert-lifecycle',    label: 'mTLS / cert lifecycle + renewal' },
      { code: 'mqtt-bridge',            label: 'MQTT bridge (north + south)' },
      { code: 'hardening',              label: 'Hardening / SC OS image' },
      { code: 'boot-first-run',         label: 'Boot / first-run flow' },
      { code: 'wifi-cellular',          label: 'WiFi / cellular networking' },
      { code: 'cross-cutting',          label: 'Cross-cutting' },
    ],
  },
  {
    code:  'machine-gateway',
    label: 'Machine Gateway (per-machine container)',
    subAreas: [
      { code: 'sas-protocol',           label: 'SAS protocol (verbatim VMIB imports)' },
      { code: 'mqtt-envelopes',         label: 'MQTT JSON envelopes (north)' },
      { code: 'serial-bridge',          label: 'Serial bridge to ESP32 HAT (south)' },
      { code: 'cert-provisioning',      label: 'Per-machine cert provisioning' },
      { code: 'cross-cutting',          label: 'Cross-cutting' },
    ],
  },
  {
    code:  'esp32-firmware',
    label: 'ESP32-P4 Firmware (HAT)',
    subAreas: [
      { code: 'sas-link',               label: 'SAS link (RJ12, Case 1)' },
      { code: 'pulse-counting',         label: 'Pulse counting / analogue (Case 3)' },
      { code: 'bv-tp-intercept',        label: 'BV/TP intercept (Case 2)' },
      { code: 'ota',                    label: 'OTA update (A/B + factory recovery)' },
      { code: 'poe-power',              label: 'PoE / power' },
      { code: 'identity-strapping',     label: 'HAT identity / strapping pins' },
      { code: 'cross-cutting',          label: 'Cross-cutting' },
    ],
  },
  {
    code:  'mac-tool',
    label: 'Mac Tool (Route.SitePreparation.Mac)',
    subAreas: [
      { code: 'qr-scanner',             label: 'QR scanner (camera)' },
      { code: 'factory-provisioning',   label: 'Factory provisioning flow' },
      { code: 'site-activation',        label: 'Site activation (Path A / B)' },
      { code: 'usb-tether',             label: 'USB tether (Personal Hotspot)' },
      { code: 'dmg-packaging',          label: 'DMG packaging / notarisation' },
      { code: 'version-chip',           label: 'About / version chip' },
      { code: 'cross-cutting',          label: 'Cross-cutting' },
    ],
  },
  {
    code:  'technician-app',
    label: 'Technician Mobile App (.NET MAUI)',
    subAreas: [
      { code: 'auth',                   label: 'Login / pairing' },
      { code: 'machine-commissioning',  label: 'Machine commissioning' },
      { code: 'sticker-scan',           label: 'QR sticker scan / affix' },
      { code: 'maintenance-log',        label: 'Maintenance log entry' },
      { code: 'tickets',                label: 'Technician tickets' },
      { code: 'photos',                 label: 'Photo capture / upload' },
      { code: 'offline-sync',           label: 'Offline mode / sync' },
      { code: 'cross-cutting',          label: 'Cross-cutting' },
    ],
  },
  {
    code:  'cashier-app',
    label: 'Cashier Mobile App (.NET MAUI)',
    subAreas: [
      { code: 'auth',                   label: 'Login' },
      { code: 'aft-transfer',           label: 'AFT money-on / money-off' },
      { code: 'bill-collection',        label: 'Bill collection' },
      { code: 'redemption',             label: 'Player redemption flow' },
      { code: 'qr-scan',                label: 'QR code scan' },
      { code: 'cross-cutting',          label: 'Cross-cutting' },
    ],
  },
  {
    code:  'reporting-app',
    label: 'Reporting Mobile App / Reports',
    subAreas: [
      { code: 'daily-summary',          label: 'Daily summary' },
      { code: 'per-machine',            label: 'Per-machine performance' },
      { code: 'cashout-register',       label: 'Cashout register' },
      { code: 'meter-snapshots',        label: 'Meter snapshots' },
      { code: 'audit-history',          label: 'Audit history' },
      { code: 'csv-pdf-export',         label: 'CSV / PDF export' },
      { code: 'cross-cutting',          label: 'Cross-cutting' },
    ],
  },
  {
    code:  'database',
    label: 'Database / migrations',
    subAreas: [
      { code: 'ef-migration',           label: 'EF migration (model snapshot)' },
      { code: 'sql-seed',               label: 'SQL seed (permissions / roles / settings)' },
      { code: 'rls',                    label: 'RLS policy' },
      { code: 'performance',            label: 'Performance / index' },
      { code: 'cross-cutting',          label: 'Cross-cutting' },
    ],
  },
  {
    code:  'ci-deploy',
    label: 'CI / build / deploy pipeline',
    subAreas: [
      { code: 'github-actions',         label: 'GitHub Actions workflow' },
      { code: 'docker-image-build',     label: 'Docker image build' },
      { code: 'azure-deploy',           label: 'Azure Container Apps deploy' },
      { code: 'sc-os-hardening',        label: 'SC OS hardening pipeline (local)' },
      { code: 'mac-tool-notarise',      label: 'Mac tool notarise / release' },
      { code: 'dev-stack',              label: 'Local DEV stack (docker compose)' },
      { code: 'cross-cutting',          label: 'Cross-cutting' },
    ],
  },
  {
    code:  'docs',
    label: 'Documentation / specs',
    subAreas: [
      { code: 'platform-spec',          label: 'ARMS Platform Specification' },
      { code: 'sc-commissioning',       label: 'SC Commissioning Plan' },
      { code: 'esp32-firmware-spec',    label: 'ESP32 Firmware Release Spec' },
      { code: 'api-reference',          label: 'ARMS API Reference' },
      { code: 'portal-form-rules',      label: 'Portal Form Rules' },
      { code: 'runbook',                label: 'Runbook (Stage 0/1/2)' },
      { code: 'readme-claude-md',       label: 'README / CLAUDE.md' },
      { code: 'cross-cutting',          label: 'Cross-cutting' },
    ],
  },
  {
    code:  'other',
    label: 'Other / not sure',
  },
];

/** Look up an area by code; undefined if not in the table. */
export function findArea(code: string | undefined | null): ComponentArea | undefined {
  if (!code) return undefined;
  return COMPONENT_AREAS.find(a => a.code === code);
}

/** Look up a sub-area within an area; undefined if not present. */
export function findSubArea(
  areaCode: string | undefined | null,
  subAreaCode: string | undefined | null,
): SubArea | undefined {
  if (!subAreaCode) return undefined;
  const area = findArea(areaCode);
  return area?.subAreas?.find(s => s.code === subAreaCode);
}
